import { useGateValue } from '@coursera-web/statsig';

import { useProductFeatures } from 'bundles/authoring/features';
import AuthoringExperiments from 'bundles/epic/clients/Authoring';
import ContentOptimizationExperiments from 'bundles/epic/clients/ContentOptimization';
import { PLATFORM_ONLY_ITEM_TYPES } from 'bundles/item-authoring/shared/platformOnlyItemTypes';
import type { ItemType } from 'bundles/item/types/ItemType';
import { IdTypes } from 'bundles/product-features';

/**
 * Function to gate item type migrations onto the item platform / item authoring framework
 */
export const isItemAuthoringFrameworkEnabledForType = (itemType: ItemType): boolean => {
  if (!itemType) {
    return false;
  }

  // 1. [SETUP] set up and add your own item-type-specific feature flag check here while in development (ie requires user/cookie targeting)
  // example epic where we gate by item type: https://tools.coursera.org/epic/experiment/HJG-kI6uEeyAw9c5depHlw
  // when use something like AuthoringExperiments.get('yourEpicName').includes?.(itemType)

  // 2. [CLEANUP] once your item type migration is completed, please clean up your feature flag check and
  // add your item to the static list referenced below.
  if (PLATFORM_ONLY_ITEM_TYPES.includes(itemType)) {
    return true;
  }
  return false;
};

// https://tools.coursera.org/epic/experiment/s9lI3W48EfC3IA6hNZeg9Q
export const isCoachForSmartIngestionEnabled = () => {
  return AuthoringExperiments.get('enableCoachForSmartIngestion');
};

// https://tools.coursera.org/epic/experiment/-G7fRBeuEfGgRA4nU0VIYQ
export const isScormRiseSmartIngestionEnabled = () => {
  return AuthoringExperiments.get('enableScormRiseSmartIngestion');
};

// https://tools.coursera.org/epic/experiment/135bswhZEfGgRA4nU0VIYQ
export const isAIGeneratedItemsEnabled = () => {
  return ContentOptimizationExperiments.get('enableAIGeneratedItems');
};

// https://tools.coursera.org/epic/experiment/RCV7AQXmEfGAfhJYNo68EQ
export const isAudioDescriptionAuthoringEnabled = () => {
  return AuthoringExperiments.get('enableAdVideoAuthoring');
};

// *****************************************************************
// **************************** Statsig ****************************
// *****************************************************************

/**
 * @see https://console.statsig.com/2aQACzHI81tdbY41wEZm3H/gates/course-builder-v3
 * @description Enabling context only asset upload
 * */
export const useCbV3ContextUploadEnabled = () => {
  return useGateValue('course-builder-v3');
};

/**
 * @see https://console.statsig.com/2aQACzHI81tdbY41wEZm3H/gates/cee-authoring-retire-programs-group
 * @see https://www.coursera.org/internal/feature-toggles?name=enableRetirePrograms&namespace=authoring
 * @description Retiring Programs to be using Groups instead - Feature Gate for Option C
 * */
export const useRetiringProgramsToGroupEnabledByPartnerId = (partnerId?: number) => {
  const isEnabledInStatsig = useGateValue('cee-authoring-retire-programs-group');

  const { features } = useProductFeatures({
    idType: IdTypes.Partner,
    id: partnerId?.toString() || '',
  });

  const enableRetirePrograms = features.get('authoring', 'enableRetirePrograms') || false;

  return isEnabledInStatsig && enableRetirePrograms;
};

/**
 * @see https://console.statsig.com/2aQACzHI81tdbY41wEZm3H/gates/plugins-enable-updated-widget-frame-view
 * @description Enabling dialog based widget frame
 * */
export const useIsPluginUpdatedUiEnabled = () => {
  return useGateValue('plugins-enable-updated-widget-frame-view');
};

/**
 * @see https://console.statsig.com/2aQACzHI81tdbY41wEZm3H/gates/plugins-enable-updated-preview-frame-in-cml?newGate=true
 * @description Enable new preview frame in plugins embedded in cml
 * */
export const useIsPluginUpdatedPreviewInCmlEnabled = () => {
  return useGateValue('plugins-enable-updated-preview-frame-in-cml');
};

/**
 * @see https://console.statsig.com/2aQACzHI81tdbY41wEZm3H/gates/plugins-enable-updated-preview-frame-in-authoring
 * @description Enabling new preview frame in authoring surfaces (other than cml)
 * */
export const useIsPluginUpdatedPreviewInAuthoringEnabled = () => {
  return useGateValue('plugins-enable-updated-preview-frame-in-authoring');
};
